/* -------------------------------------------------------------------------- */
/*  Robins & Morton – Sage 300 import (P/C record pairs)                        */
/* -------------------------------------------------------------------------- *
 *  - "*" header from project name + address                                   *
 *  - Line items split into LMSEO buckets; each emits a "P" phase record and a   *
 *    "C" cost record. Phase code comes from the Phase sort field (leading zeros *
 *    stripped). Markup category from the Markup Category sort field, else "O".   *
 *  - Returns [stringLines, "txt"]                                              */
/* -------------------------------------------------------------------------- */

import { CategoryMap } from "./categories";
import { BudgetImportConfig, BudgetRow } from "./framework/types";
import { stripLeadingZeros } from "./framework/helpers";

const CATEGORY_MAP: CategoryMap = {
  labor: "LR",
  material: "O",
  equip: "M",
  sub: "S",
  other: "R",
  total: "S", // placeholder
};

/** Quoted, zero-stripped phase code, or "" when the row has no Phase sort value. */
const phaseCode = (row: BudgetRow): string => (row["Phase_code"] ? `"${stripLeadingZeros(row["Phase_code"])}"` : "");

export const robinsMortonConfig: BudgetImportConfig = {
  format: "sage",
  requires: { project: true },
  sage: {
    ext: "txt",
    categoryMap: CATEGORY_MAP,
    buildHeader: ({ project }) =>
      `*,"${project!.name.slice(0, 5)}","${project!.name}", "${project!.address?.split(", ")[0] || ""}", "${project!.city}", "${project!.state}", "${project!.zip}"`,
    renderLineItem: ({ row, category, unitCost, ctx }) => {
      const total = row.quantity * unitCost;
      const code = phaseCode(row);
      return [
        `"P",${code},,,${ctx.dateNum}, `,
        // Coerce null/undefined uom to "": the migrated data source returns null where the legacy
        // source returned "", which would render as the literal text "null".
        `"C",${code},,"${category}",${ctx.dateNum},${row.quantity},"${row.uom ?? ""}",${total.toFixed(2)}`,
      ];
    },
    renderMarkup: ({ row, ctx }) => {
      const code = phaseCode(row);
      const markupCategory = row["Markup Category_code"] ? `"${row["Markup Category_code"]}"` : "O";
      return [
        `"P",${code},,,${ctx.dateNum}`,
        `"C",${code},,${markupCategory},${ctx.dateNum},,,${Number(row.total).toFixed(2)}`,
      ];
    },
  },
};
